import { useCallback, useEffect, useRef, useState } from "react";
import { usePermissions } from "../providers/authProvider";
import { Api } from "../api";
import { SITE_DATA_PARTITIONS } from "../../../types";
import { ShipmentData } from "../../../types/shipments";
import { ShipmentRequest } from "./useOrder";

export const useShipment = ({
  slug,
  permissions,
}: {
  slug?: string;
  permissions?: ReturnType<typeof usePermissions>;
}) => {
  const shouldRequestShipmentData = useRef(!!slug);

  useEffect(() => {
    if (slug) {
      shouldRequestShipmentData.current = true;
    }
  }, [slug]);
  const [shipmentRequest, setShipmentRequest] = useState<ShipmentRequest>({
    status: shouldRequestShipmentData.current ? "loading" : "no-slug",
  });

  const request = useCallback(() => {
    if (slug && !permissions?.loading) {
      setShipmentRequest({
        status: slug ? "loading" : "no-slug",
      });

      Api.get<ShipmentData>({
        type: SITE_DATA_PARTITIONS.SHIPMENT,
        slug,
      })
        .then((resp) =>
          resp.success
            ? setShipmentRequest((req) => ({
                status: "present" as const,
                shipment: resp.data,
                shipmentSlug: slug,
              }))
            : setShipmentRequest((req) => ({
                status: "server-error" as const,
                error: resp.error,
                shipmentSlug: slug,
              }))
        )
        .catch((e) =>
          setShipmentRequest((req) => ({
            status: "request-error" as const,
            error: e,
            shipmentSlug: slug,
          }))
        );
    }
  }, [slug, permissions?.loading, setShipmentRequest]);

  useEffect(() => {
    if (shouldRequestShipmentData.current && slug && !permissions?.loading) {
      shouldRequestShipmentData.current = false;
      request();
    }
  }, [shouldRequestShipmentData, request, slug, permissions?.loading]);
  return [shipmentRequest, request] as const;
};
